import { Plus, Threads } from '@/components/ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ThreadDTO } from '@clanfire/shared';
import { Button, IconButton } from '@/components/ui/Button';
import { EmptyState, ErrorState } from '@/components/ui/Feedback';
import { Popover } from '@/components/ui/Popover';
import { Spinner } from '@/components/ui/Spinner';
import { formatRelative } from '@/lib/time';
import { cn } from '@/lib/utils';
import { useMemberName } from '@/stores/servers';
import { useReadStates } from '@/stores/social';
import { isThreadUnread, threadPath, useThreads } from '@/stores/threads';
import { useUi } from '@/stores/ui';
import { useUser } from '@/stores/users';

/** Top-bar button listing a channel's threads, with a way to start a new one. */
export function ThreadsButton({ channelId, canCreate }: { channelId: string; canCreate: boolean }) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const status = useThreads((s) => s.lists[channelId]);
  const all = useThreads((s) => s.threads);
  const reads = useReadStates((s) => s.channels);
  const openModal = useUi((s) => s.openModal);
  const navigate = useNavigate();

  // Loaded once per channel; socket events keep it current after that.
  useEffect(() => {
    void useThreads.getState().loadChannel(channelId);
  }, [channelId]);

  const { active, archived, unread } = useMemo(() => {
    const threads = Object.values(all)
      .filter((t) => t.parentId === channelId)
      .sort((a, b) => (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt));
    return {
      active: threads.filter((t) => !t.archivedAt),
      archived: threads.filter((t) => t.archivedAt),
      unread: threads.some((t) => !t.archivedAt && isThreadUnread(t, reads[t.id]?.lastReadAt)),
    };
  }, [all, channelId, reads]);

  const go = (thread: ThreadDTO) => {
    setOpen(false);
    navigate(threadPath(thread));
  };

  return (
    <>
      <span className="relative">
        <IconButton ref={anchor} label="Threads" active={open} onClick={() => setOpen((v) => !v)}>
          <Threads size={20} />
        </IconButton>
        {unread && <span className="pointer-events-none absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-accent ring-2 ring-surface" aria-label="Unread threads" />}
      </span>
      <Popover open={open} onClose={() => setOpen(false)} anchor={anchor} placement="bottom-end" label="Threads" className="flex w-[min(400px,calc(100vw-16px))] flex-col">
        <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
          <h2 className="font-bold">Threads</h2>
          {canCreate && (
            <Button
              size="sm"
              icon={<Plus size={15} />}
              onClick={() => {
                setOpen(false);
                openModal({ type: 'createThread', channelId });
              }}
            >
              New thread
            </Button>
          )}
        </div>
        <div className="max-h-[min(480px,65dvh)] overflow-y-auto p-1.5">
          {status === 'error' ? (
            <ErrorState message="Could not load threads." onRetry={() => void useThreads.getState().loadChannel(channelId, true)} />
          ) : status !== 'loaded' ? (
            <div className="flex justify-center py-10 text-fg-muted">
              <Spinner />
            </div>
          ) : active.length + archived.length === 0 ? (
            <EmptyState
              icon={<Threads size={26} />}
              title="No threads yet"
              body={canCreate ? 'Start one from any message (hover it and pick the thread button), or with New thread.' : 'Side conversations started here will show up in this list.'}
            />
          ) : (
            <>
              {active.length > 0 && <ThreadList title="Active" threads={active} reads={reads} onOpen={go} />}
              {archived.length > 0 && <ThreadList title="Archived" threads={archived} reads={reads} onOpen={go} />}
            </>
          )}
        </div>
      </Popover>
    </>
  );
}

function ThreadList({
  title,
  threads,
  reads,
  onOpen,
}: {
  title: string;
  threads: ThreadDTO[];
  reads: Record<string, { lastReadAt: string }>;
  onOpen: (thread: ThreadDTO) => void;
}) {
  return (
    <section aria-label={`${title} threads`} className="mb-1">
      <h3 className="px-2.5 pt-2 pb-1 text-[11px] font-bold tracking-wider text-fg-muted uppercase">
        {title} — {threads.length}
      </h3>
      <ul>
        {threads.map((t) => (
          <ThreadRow key={t.id} thread={t} unread={isThreadUnread(t, reads[t.id]?.lastReadAt)} onOpen={() => onOpen(t)} />
        ))}
      </ul>
    </section>
  );
}

function ThreadRow({ thread, unread, onOpen }: { thread: ThreadDTO; unread: boolean; onOpen: () => void }) {
  const owner = useUser(thread.ownerId);
  const ownerName = useMemberName(thread.serverId, thread.ownerId) ?? owner?.displayName;
  return (
    <li>
      <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left hover:bg-hover">
        <Threads size={18} className={cn('shrink-0', unread ? 'text-accent' : 'text-fg-muted')} />
        <span className="min-w-0 flex-1">
          <span className={cn('block truncate text-sm', unread ? 'font-bold text-fg' : 'font-semibold text-fg-soft')}>{thread.name}</span>
          <span className="block truncate text-xs text-fg-muted">
            {ownerName ? `${ownerName} · ` : ''}
            {thread.messageCount} message{thread.messageCount === 1 ? '' : 's'}
            {thread.lastMessageAt && ` · ${formatRelative(thread.lastMessageAt)}`}
          </span>
        </span>
        {unread && <span className="h-2 w-2 shrink-0 rounded-full bg-accent" aria-label="Unread" />}
      </button>
    </li>
  );
}
